import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { isEmpty } from 'lodash';

import { DEFAULT_PAGE_SIZE } from '@/constants/common';
import type { CloudSourceTreeNode } from '@/features/CloudSourcesTable/CloudSourcesTable.types';
import type {
  DeviceDetailsDTO,
  DeviceExportDataModel,
  DeviceModel,
  DevicesDTOKeys,
  GroupedDevices,
  SourceModel
} from '@/features/DevicesTable';
import type {
  LimaSourcesDTOKeys,
  LimaSourcesResponseModal
} from '@/features/DevicesTable/DevicesTable.types';
import type { SearchFilters } from '@/features/SearchBar';
import type { LimaDetailedSourceModel } from '@/features/SourceCreate/SourceCreate.types';

const selectedCloudSourceRowsAtom = atom<CloudSourceTreeNode[]>([]);

const devicesAtom = atom<DeviceModel[]>([]);
const groupedDevicesAtom = atom<GroupedDevices[]>([]);
const isCustomerCloudSourcePageAtom = atom(false);
const selectedDeviceAtom = atom<DeviceModel | DeviceDetailsDTO>(
  {} as DeviceModel
);
const selectedSourceAtom = atom<SourceModel>({} as SourceModel);
const selectedCloudSourceAtom = atom<LimaSourcesResponseModal>(
  {} as LimaSourcesResponseModal
);
const cloudSourcesAtom = atom<LimaSourcesResponseModal[]>([]);
const hoveredRowIdAtom = atom<string | null>(null);

const customEventAtom = atom(0);
const selectedDeviceDetailsAtom = atom<DeviceDetailsDTO>(
  {} as DeviceDetailsDTO
);
const selectedDeviceEditDetailsAtom = atom<DeviceDetailsDTO>(
  {} as DeviceDetailsDTO
);
const selectedSourceDetailsAtom = atom<LimaDetailedSourceModel>(
  {} as LimaDetailedSourceModel
);

const selectedMicrosoftSourceDetailsAtom = atom<LimaDetailedSourceModel[]>(
  [] as LimaDetailedSourceModel[]
);

const diffViewModeAtom = atom('split');
const selectedDeviceIdAtom = atom('');
const enableVulScanAtom = atom(false);
const enableFetchObjectsAtom = atom(false);
const sourceStatusAtom = atom(false);
const syncInProgressAtom = atom(false);

export const devicesTableRowsAtom = atomWithStorage(
  'DeviceTablesRows',
  DEFAULT_PAGE_SIZE
);
export const cloudSourcesTableRowsAtom = atomWithStorage(
  'DeviceTablesRows',
  DEFAULT_PAGE_SIZE
);
export const devicesFiltersAtom = atom<SearchFilters>({});
export const limaSourcesFiltersAtom = atom<SearchFilters>({});

export const filteredLimaSourcesAtom = atom((get) => {
  const limaSources = get(cloudSourcesAtom);
  const filters = get(limaSourcesFiltersAtom);

  if (isEmpty(filters)) return limaSources;
  return limaSources.filter((source: LimaSourcesResponseModal) =>
    Object.keys(filters).every((k) =>
      filters[k].some((f) => {
        // use JSON.stringify to search for sources values
        if (k === 'any') {
          return JSON.stringify(source)
            .toLocaleLowerCase()
            .includes(f.toLocaleLowerCase());
        }
        // filter by source_type_id
        if (k === 'source_type_id') {
          return source.source_type_id?.toString() === f;
        }
        // filter by status
        if (k === 'status') {
          return source.status === f;
        }
        // filter by active state (activetaed / deactivated)
        if (k === 'active_state') {
          return source.active_state === f;
        }

        return source[k as LimaSourcesDTOKeys] === f;
      })
    )
  );
});

export const filteredDevicesAtom = atom((get) => {
  const devices = get(devicesAtom);
  const filters = get(devicesFiltersAtom);

  if (isEmpty(filters)) return devices;

  return devices.filter((device: DeviceModel) =>
    Object.keys(filters).every((k) =>
      filters[k].some((f) => {
        // use JSON.stringify to search for product values
        if (k === 'any') {
          return JSON.stringify(device)
            .toLocaleLowerCase()
            .includes(f.toLocaleLowerCase());
        }
        // filter by device_type_id
        if (k === 'device_type_id') {
          return device.device_type_id?.toString() === f;
        }
        // filter by status
        if (k === 'status') {
          return device.status === f;
        }
        // filter by log sources
        if (k === 'sources') {
          return (
            device as {
              sources?: { connection_inputs?: { sources?: string[] } }[];
            }
          )?.sources?.[0]?.connection_inputs?.sources?.includes(f);
        }

        return device[k as DevicesDTOKeys] === f;
      })
    )
  );
});

export const devicesExportAtom = atom<DeviceExportDataModel[]>((get) => {
  const devices = get(filteredDevicesAtom);

  return devices.map(
    (device): DeviceExportDataModel => ({
      DeviceID: device?.device_uid,
      HostName: device?.host_name,
      DisplayName: device?.display_name || '',
      Description: device?.description || '',
      DeviceType: device?.device_type?.type || '',
      DeviceSize: device?.device_size?.device_size || '',
      IpAddress: device?.ip_address || '',
      LastSeen: device?.last_seen || '',
      OrganizationCode:
        device?.device_type_id === 1
          ? 'ponhq'
          : device?.organization?.label || '',
      OrganizationName:
        device?.device_type_id === 1
          ? 'Pondurance'
          : device?.organization?.name || '',
      SourceCount: device?.source_count || 0,
      Status: device?.status || '',
      StatusUpdatedAt: device?.status_updated_at_display || ''
    })
  );
});

enum CustomEventTypes {
  NONE = 0,
  SHOW_CREATE_SOURCE = 1,
  REFRESH_DEVICE_DETAIL = 2,
  RESET_CLOSE = 3,
  REFRESH_DEVICE_LISTING = 4,
  CLOSE_DEVICE_DETAIL = 5,
  REFRESH_CLOUD_SOURCES = 6,
  REFRESH_CLOUD_SOURCE_DETAIL = 7
}

export {
  CustomEventTypes,
  cloudSourcesAtom,
  customEventAtom,
  devicesAtom,
  diffViewModeAtom,
  enableFetchObjectsAtom,
  enableVulScanAtom,
  groupedDevicesAtom,
  hoveredRowIdAtom,
  isCustomerCloudSourcePageAtom,
  selectedCloudSourceAtom,
  selectedCloudSourceRowsAtom,
  selectedDeviceAtom,
  selectedDeviceDetailsAtom,
  selectedDeviceEditDetailsAtom,
  selectedDeviceIdAtom,
  selectedMicrosoftSourceDetailsAtom,
  selectedSourceAtom,
  selectedSourceDetailsAtom,
  sourceStatusAtom,
  syncInProgressAtom
};
export default devicesAtom;
